import { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { TYPO } from '@/constants/typography';
import { ChevronLeft, MapPin, Users, Trophy, TrendingUp } from 'lucide-react-native';
import type { Organization, OrgRole } from '@/types/database';

interface ClubMember {
  user_id: string;
  role: OrgRole;
  display_name: string;
  handle: string | null;
  avatar_url: string | null;
  duel_standard_strength_kg: number | null;
  subscription_tier: string | null;
}

export default function ClubProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { theme } = useTheme();
  const { profile: currentUser } = useAuth();
  const c = theme.colors;

  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [isMember, setIsMember] = useState(false);

  const loadClub = useCallback(async () => {
    if (!id) return;
    
    // First check if this ID matches a gym (not an org)
    // If so, redirect to the gym page
    try {
      const { data: gymData } = await supabase
        .from('gyms')
        .select('id')
        .eq('id', id)
        .maybeSingle();
        
      if (gymData) {
        router.replace(`/gym/${id}` as any);
        return;
      }
    } catch (error) {
      // Continue to org loading if gym check fails
    }
    
    setLoading(true);
    try {
      const { data: orgData } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (orgData) setOrg(orgData as Organization);

      const { data: memberData } = await supabase
        .from('organization_memberships')
        .select('user_id, role')
        .eq('org_id', id)
        .eq('status', 'active');

      if (memberData && memberData.length > 0) {
        const userIds = memberData.map((m: any) => m.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, handle, avatar_url, duel_standard_strength_kg, subscription_tier')
          .in('id', userIds);

        const merged: ClubMember[] = memberData.map((m: any) => {
          const p = profiles?.find((pr: any) => pr.id === m.user_id);
          return {
            user_id: m.user_id,
            role: m.role,
            display_name: p?.display_name ?? 'Member',
            handle: p?.handle ?? null,
            avatar_url: p?.avatar_url ?? null,
            duel_standard_strength_kg: p?.duel_standard_strength_kg ?? null,
            subscription_tier: p?.subscription_tier ?? null,
          };
        });
        setMembers(merged);

        if (currentUser?.id) {
          setIsMember(userIds.includes(currentUser.id));
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [id, currentUser?.id]);

  useEffect(() => { loadClub(); }, [loadClub]);

  const handleJoin = async () => {
    if (!id || !currentUser?.id || joining) return;
    setJoining(true);
    try {
      await supabase.from('organization_memberships').insert({
        org_id: id,
        user_id: currentUser.id,
        role: 'member',
        status: 'active',
        is_primary_for_type: false,
      });
      setIsMember(true);
      await loadClub();
    } catch {
      // ignore
    } finally {
      setJoining(false);
    }
  };

  const accentColor = org?.accent_color || c.accent;
  const memberCount = members.length;
  const coaches = members.filter((m) => m.subscription_tier === 'coach_pro' || m.role === 'coach');
  const leaderboard = [...members]
    .filter((m) => m.duel_standard_strength_kg && m.duel_standard_strength_kg > 0)
    .sort((a, b) => (b.duel_standard_strength_kg ?? 0) - (a.duel_standard_strength_kg ?? 0))
    .slice(0, 5);
  const avgStrength =
    leaderboard.length > 0
      ? Math.round(leaderboard.reduce((s, m) => s + (m.duel_standard_strength_kg ?? 0), 0) / leaderboard.length)
      : 0;

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator color={c.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!org) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <View style={[styles.navBar, { borderBottomColor: c.border }]}>
          <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
            <ChevronLeft size={22} color={c.textSecondary} />
          </TouchableOpacity>
          <Text style={[styles.navTitle, { color: c.textSecondary }]}>Club</Text>
          <View style={{ width: 22 }} />
        </View>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ color: c.textSecondary, fontSize: 16 }}>Club not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Nav */}
      <View style={[styles.navBar, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
          <ChevronLeft size={22} color={c.textSecondary} />
        </TouchableOpacity>
        <Text style={[styles.navTitle, { color: c.textSecondary }]}>Club</Text>
        <View style={{ width: 22 }} />
      </View>

      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {/* Logo / Avatar */}
        <View style={styles.headerSection}>
          {org.logo_url ? (
            <Image source={{ uri: org.logo_url }} style={styles.logo} />
          ) : (
            <View style={[styles.logoFallback, { backgroundColor: accentColor + '25' }]}>
              <Text style={[styles.logoInitial, { color: accentColor }]}>
                {org.name.charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <Text style={[styles.clubName, { color: c.text }]}>{org.name}</Text>
          {org.description ? (
            <Text style={[styles.description, { color: c.textSecondary }]}>{org.description}</Text>
          ) : null}
        </View>

        {/* Stats Row */}
        <View style={styles.statsRow}>
          <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Users size={16} color={accentColor} />
            <Text style={[styles.statValue, { color: c.text }]}>{memberCount}</Text>
            <Text style={[styles.statLabel, { color: c.textTertiary }]}>Members</Text>
          </View>
          <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <TrendingUp size={16} color={accentColor} />
            <Text style={[styles.statValue, { color: c.text }]}>{avgStrength || '—'}</Text>
            <Text style={[styles.statLabel, { color: c.textTertiary }]}>Avg Strength</Text>
          </View>
          <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Trophy size={16} color={accentColor} />
            <Text style={[styles.statValue, { color: c.text }]}>{leaderboard.length}</Text>
            <Text style={[styles.statLabel, { color: c.textTertiary }]}>Ranked</Text>
          </View>
        </View>

        {/* Coaches / Staff */}
        {coaches.length > 0 && (
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.sectionTitle, { color: c.textTertiary }]}>COACHES & STAFF</Text>
            {coaches.map((coach) => (
              <TouchableOpacity
                key={coach.user_id}
                style={styles.memberRow}
                onPress={() => coach.handle && router.push(`/u/${coach.handle}` as any)}
                activeOpacity={0.7}
              >
                {coach.avatar_url ? (
                  <Image source={{ uri: coach.avatar_url }} style={styles.memberAvatar} />
                ) : (
                  <View style={[styles.memberAvatarFallback, { backgroundColor: accentColor + '20' }]}>
                    <Text style={[styles.memberInitial, { color: accentColor }]}>
                      {coach.display_name.charAt(0).toUpperCase()}
                    </Text>
                  </View>
                )}
                <View style={{ flex: 1 }}>
                  <Text style={[styles.memberName, { color: c.text }]}>{coach.display_name}</Text>
                  {coach.handle && (
                    <Text style={[styles.memberHandle, { color: c.textTertiary }]}>@{coach.handle}</Text>
                  )}
                </View>
                <View style={[styles.roleBadge, { backgroundColor: accentColor + '20' }]}>
                  <Text style={[styles.roleText, { color: accentColor }]}>
                    {coach.role === 'coach' ? 'Coach' : 'Staff'}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Top 5 Leaderboard */}
        {leaderboard.length > 0 && (
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.sectionTitle, { color: c.textTertiary }]}>TOP LEADERBOARD</Text>
            {leaderboard.map((m, i) => (
              <TouchableOpacity
                key={m.user_id}
                style={styles.memberRow}
                onPress={() => m.handle && router.push(`/u/${m.handle}` as any)}
                activeOpacity={0.7}
              >
                <Text style={[styles.rankNum, { color: i === 0 ? accentColor : c.textTertiary }]}>
                  #{i + 1}
                </Text>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.memberName, { color: c.text }]}>{m.display_name}</Text>
                </View>
                <Text style={[styles.scoreText, { color: c.text }]}>
                  {Math.round(m.duel_standard_strength_kg ?? 0)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Join Button */}
        {!isMember && currentUser?.id && (
          <TouchableOpacity
            style={[styles.joinBtn, { backgroundColor: accentColor }]}
            onPress={handleJoin}
            disabled={joining}
            activeOpacity={0.7}
          >
            <Text style={[styles.joinBtnText, { color: '#fff' }]}>
              {joining ? 'Joining…' : 'Join This Club'}
            </Text>
          </TouchableOpacity>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  navBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  navTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 16,
  },
  headerSection: {
    alignItems: 'center',
    gap: 8,
  },
  logo: {
    width: 80,
    height: 80,
    borderRadius: 4,
  },
  logoFallback: {
    width: 80,
    height: 80,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoInitial: {
    fontSize: 36,
    fontFamily: TYPO.heading.bold,
  },
  clubName: {
    fontSize: 26,
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
  },
  description: {
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 10,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  statCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    alignItems: 'center',
    gap: 4,
  },
  statValue: {
    fontSize: 20,
    fontFamily: TYPO.heading.bold,
  },
  statLabel: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
  },
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 10,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: TYPO.heading.bold,
    letterSpacing: 1,
    marginBottom: 4,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
  },
  memberAvatar: {
    width: 36,
    height: 36,
    borderRadius: 2,
  },
  memberAvatarFallback: {
    width: 36,
    height: 36,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberInitial: {
    fontSize: 15,
    fontFamily: TYPO.heading.bold,
  },
  memberName: {
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
  },
  memberHandle: {
    fontSize: 11,
    fontWeight: '500',
  },
  roleBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 2,
  },
  roleText: {
    fontSize: 11,
    fontWeight: '700',
  },
  rankNum: {
    fontSize: 14,
    fontFamily: TYPO.heading.bold,
    width: 30,
  },
  scoreText: {
    fontSize: 14,
    fontFamily: TYPO.heading.bold,
  },
  joinBtn: {
    paddingVertical: 14,
    borderRadius: 2,
    alignItems: 'center',
  },
  joinBtnText: {
    fontSize: 16,
    fontWeight: '800',
  },
});
